import { useRegenerateAssistantResponse } from '@modules/oracle/hooks';
import { OracleSearchActionType } from '@modules/oracle/store/oracleActionType';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { trackWisdomEvents, updatePromptByID } from '@modules/oracle/utils';
import { AutorenewRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { CircularProgress, IconButton, Stack, Tooltip } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface RegenerateResponseButtonProps {
  question: string;
  promptID: string;
  variant: 'icon' | 'button';
}

export function RegenerateResponseButton({
  question,
  promptID,
  variant,
}: RegenerateResponseButtonProps) {
  const [regenerateAssistantResponse, { isLoading, responseID }] =
    useRegenerateAssistantResponse();
  const dispatch = useOracleSearchStore(({ dispatch }) => dispatch);
  const promptByID = useOracleSearchStore(({ data }) => data.promptByID);
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const dependencies = {
    promptByID,
    promptID,
    question,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  useEffect(() => {
    if (!isLoading && responseID && dependenciesRef.current.promptByID) {
      const updatedPromptByID = updatePromptByID({
        newPromptID: responseID,
        promptByID: dependenciesRef.current.promptByID,
        editedFromResponseID: dependenciesRef.current.promptID,
      });

      dispatch({
        type: OracleSearchActionType.SET_DATA,
        payload: {
          promptByID: updatedPromptByID,
          currentPromptID: responseID,
        },
      });
    }
  }, [isLoading, responseID, dispatch, userID, tenantID]);

  const onClick = () => {
    regenerateAssistantResponse({
      question,
      promptID,
    });
    trackWisdomEvents({
      eventName: 'Wisdom - Regenerate Response',
      searchText: question,
    });
  };

  if (variant === 'icon') {
    return !isLoading ? (
      <Tooltip arrow title="Regenerate">
        <IconButton size="small" color="primary" onClick={onClick}>
          <AutorenewRounded sx={{ fontSize: 'inherit' }} color="inherit" />
        </IconButton>
      </Tooltip>
    ) : (
      <Stack pl={1}>
        <CircularProgress size={18} />
      </Stack>
    );
  }

  return (
    <LoadingButton
      size="small"
      color="primary"
      onClick={onClick}
      startIcon={<AutorenewRounded />}
      loading={isLoading}
    >
      Regenerate
    </LoadingButton>
  );
}
